import React from 'react';
import type { ComponentConfig, Fields } from '@puckeditor/core';
import styled from 'styled-components';

import { interpolateFromPuck } from '../../interpolation';
import { brandPickerField } from '../../fields/widgets/brand-picker-field';
import { selectField } from '../../fields/widgets/select-field';
import {
  carouselTypeDefinition,
  CATEGORY_OPTIONS,
  PRODUCT_CAROUSEL_TYPE_OPTIONS,
  resolveCarouselHeading,
  resolveSubcategory,
  subcategoryOptionsForCategory,
} from './carousel-types';
import type { ProductCarouselProps } from './carousel-types';
import { carouselTypeRenderer } from './type-renderers';

/**
 * Declared here rather than imported from the menu-landing model's `shared.ts`. This
 * block is model-neutral and sits alongside the other generic page blocks, so it must
 * not reach into a particular model's internals.
 */
const SHOW_HEADING_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

/**
 * The consolidated Product Carousel: one block whose remaining fields follow from a Type
 * selection, replacing the ten per-type carousel blocks the menu landing model used to
 * register.
 *
 * Model-neutral by design, and registered by both the menu landing and standalone page
 * builders — the same carousel is a Page Section wherever an author drops it. It needs a
 * resolved dispensary, which every context provides: published pages carry a `cName` on
 * the route, and the editor canvas gets one seeded by the "Previewing as" control.
 *
 * Types are wired up a ticket at a time so each one's field set and its store-scoping
 * consequences get reviewed on their own. Live today: the platform-decided types in
 * ./type-renderers (ECOMM-12683), the category/subcategory types (ECOMM-12684), and brand
 * (ECOMM-12685 — reviewed and merged as its own unit in #22049; its diff rides along here
 * because that PR was based on this branch, so both arrive together).
 *
 * Brand's store-scoping consequence is the one worth knowing: only *global* brands are
 * offered, because a store-local `brandId` renders an empty carousel on sibling stores.
 * See `fields/widgets/brand-picker-widget.tsx` for the full rationale and the client-side
 * defence in depth.
 *
 * Still inert, and rendering the editor placeholder: collection and custom.
 */

const EditorPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 24px;
  border: 1px dashed var(--dcm-cms-placeholder-border-color, #c2c9cd);
  border-radius: var(--dcm-cms-placeholder-radius, 8px);
  background: var(--dcm-cms-placeholder-bg, #f0f2f4);
  color: var(--dcm-cms-placeholder-color, #646d72);
  font-size: 14px;
`;

const PlaceholderHeading = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: var(--dcm-cms-placeholder-heading-color, #393c40);
`;

/** Shown in place of the resolved heading when there is none to show. */
const UNTITLED_PLACEHOLDER_HEADING = 'Product Carousel';

/**
 * Editor-only wrapper for a type that DOES have a renderer.
 *
 * Every carousel hides itself when it has nothing to show — no matching products for
 * the previewed store, or a visibility guard like "signed out" for Recently Viewed. On
 * the storefront that is exactly right. In the editor it collapsed the block to a
 * zero-height box: Puck hit-tests on the bounding rect, so the block became impossible
 * to select or drag, and to an author it simply vanished the moment they chose that
 * type — with no hint that the choice had worked and the store just had no products.
 *
 * The notice below is always rendered and hides itself the moment the carousel renders
 * anything: the carousel's own DOM becomes a sibling, so the notice stops being an only
 * child. That keeps it self-correcting — it disappears on its own once products arrive,
 * and even while the carousel is still showing loading skeletons, which are real
 * elements — without having to thread an "am I empty?" signal back out of carousels that
 * decide it internally.
 *
 * Deliberately a real element rather than `::after` generated content: it is readable by
 * assistive tech, and it carries no CSS string to escape. If the rule below ever failed
 * to apply, the notice would show on a working carousel — visually noisy, but the block
 * stays visible and selectable, which is the safer way for this to break.
 *
 * The wrapper is never rendered on the published page, so the storefront DOM is
 * unchanged.
 */
const EditorCarouselSlot = styled.div`
  & > [data-empty-notice]:not(:only-child) {
    display: none;
  }
`;

const EmptyCarouselNotice = styled.p`
  margin: 0;
  padding: 24px;
  border: 1px dashed var(--dcm-cms-placeholder-border-color, #c2c9cd);
  border-radius: var(--dcm-cms-placeholder-radius, 8px);
  background: var(--dcm-cms-placeholder-bg, #f0f2f4);
  color: var(--dcm-cms-placeholder-color, #646d72);
  font-size: 14px;
`;

export const productCarouselConfig: ComponentConfig<ProductCarouselProps> = {
  label: 'Product Carousel',
  ai: {
    instructions:
      'A row of products on the storefront. Pick the Type first — it decides which products appear and which other settings apply.',
  },
  fields: {
    carouselType: selectField(
      'Type',
      PRODUCT_CAROUSEL_TYPE_OPTIONS,
      'Which products the carousel shows. Everything else about the carousel follows from this, so always set it.'
    ),
    heading: {
      type: 'text',
      label: 'Heading',
      ai: {
        instructions:
          'Leave empty to use the heading generated from the selected type. Supports {{name}} and {{address}} placeholders.',
      },
    },
    showHeading: selectField(
      'Show heading',
      SHOW_HEADING_OPTIONS,
      'Set to No to render the carousel with no heading at all, which is different from leaving the Heading field empty.'
    ),
    // category, subcategory, brandId, and brandName are declared here to satisfy
    // TypeScript's requirement that all props in ProductCarouselProps have a
    // corresponding field entry. resolveFields controls when they actually appear in
    // the editor panel: only for the types that declare them in extraFields.
    category: selectField('Category', CATEGORY_OPTIONS, 'The product category this carousel shows.'),
    subcategory: selectField('Subcategory', [], 'The subcategory to feature. Options depend on the selected Category.'),
    brandId: brandPickerField,
    brandName: { type: 'text', label: 'Brand Name', ai: { exclude: true } },
  },
  /**
   * Drops a stored subcategory that is not valid for the current category.
   *
   * `resolveFields` rebuilds the Subcategory *field* when the category changes, but it cannot
   * touch stored *props* -- so the previous category's value survived and rendered through
   * `selectField`'s unlisted-value branch as `Custom (bulk-flower)`. That branch exists for
   * genuinely hand-authored values, so it cannot itself distinguish "deliberate" from "stale";
   * the carousel owns that judgement because only it knows subcategories are scoped by category.
   *
   * The renderer already re-resolves defensively (`SubcategoryRender`), so this never changed
   * what shoppers saw -- it only stopped the panel from asserting a value the block would ignore.
   */
  resolveData: ({ props }) => {
    if (!props.subcategory) {
      return { props };
    }
    const validSubcategory = resolveSubcategory(props.category, props.subcategory);
    if (validSubcategory === props.subcategory) {
      return { props };
    }
    return { props: { ...props, subcategory: '' } };
  },
  /**
   * Merges per-type `extraFields` into the field set whenever the type changes.
   *
   * Signature: `(data, { fields }) => Fields<ProductCarouselProps>`
   *
   * The mechanism is declarative: each type definition contributes an optional
   * `extraFields` map; `resolveFields` just spreads whatever the current type declares.
   * Brand/Collection/Custom (ECOMM-12685/86/87) add their own `extraFields` to their
   * type definitions without touching this function at all.
   *
   * For the subcategory type, the subcategory field's option list is rebuilt from the
   * currently selected category each time either changes, keeping the dropdown in sync.
   */
  resolveFields: (data, { fields }) => {
    const typeDef = carouselTypeDefinition(data.props.carouselType);
    const extra = typeDef?.extraFields ?? {};

    // Rebuild the subcategory field's options from the current category so the dropdown
    // always reflects what is valid for the selected category.
    const subcategoryOptions = subcategoryOptionsForCategory(data.props.category);
    const resolvedExtra =
      'subcategory' in extra
        ? {
            ...extra,
            subcategory: selectField(
              'Subcategory',
              subcategoryOptions,
              'The subcategory to feature. Options depend on the selected Category.'
            ),
          }
        : extra;

    // `fields` (the static config) includes category, subcategory, brandId, and
    // brandName to satisfy TypeScript, but they must only appear in the panel for
    // types that declare them in extraFields. Destructure them out of the base so
    // they are never present unless the type re-introduces them via resolvedExtra.
    const { brandId: _brand, brandName: _brandName, category: _cat, subcategory: _sub, ...baseFields } = fields;
    // Cast: TypeScript requires the full Fields<ProductCarouselProps> shape, but
    // Puck correctly treats the returned set as the complete field list for the
    // editor panel — category/subcategory are absent for types that do not need them.
    return { ...baseFields, ...resolvedExtra } as Fields<ProductCarouselProps>;
  },
  defaultProps: {
    brandId: '',
    brandName: '',
    category: '',
    carouselType: '',
    heading: '',
    showHeading: 'true',
    subcategory: '',
  },
  render: ({
    brandId = '',
    brandName = '',
    category = '',
    carouselType = '',
    heading = '',
    showHeading = 'true',
    subcategory = '',
    puck,
  }) => {
    const authoredHeading = resolveCarouselHeading({
      brandId,
      brandName,
      category,
      carouselType,
      heading,
      showHeading,
      subcategory,
    });
    // Interpolation has to preserve `undefined` rather than collapse it to '', since
    // "no heading" and "empty heading" mean different things downstream.
    const resolvedHeading = authoredHeading === undefined ? undefined : interpolateFromPuck(authoredHeading, puck);

    const typeDef = carouselTypeDefinition(carouselType);
    const typeLabel = typeDef?.label;
    const TypeRender = carouselTypeRenderer(carouselType);

    // A type may require author-supplied fields (category, subcategory, brand, …) before
    // it can issue a meaningful product query. When the predicate says "not yet configured",
    // skip the delegate carousel entirely — on the storefront to avoid a doomed query, and
    // in the editor to give the author an actionable placeholder instead of a misleading
    // "this store has nothing to show" notice.
    const configured = typeDef?.isConfigured
      ? typeDef.isConfigured({ brandId, brandName, category, carouselType, heading, showHeading, subcategory })
      : true;

    if (TypeRender && !configured) {
      if (!puck.isEditing) {
        return <></>;
      }
      const missingFields = typeDef?.missingFieldsMessage ?? 'the required fields';
      return (
        <EditorPlaceholder>
          <PlaceholderHeading>{resolvedHeading ?? UNTITLED_PLACEHOLDER_HEADING}</PlaceholderHeading>
          <span>
            {`Choose ${missingFields} in the settings panel.`} This block will not render on the published page.
          </span>
        </EditorPlaceholder>
      );
    }

    if (TypeRender) {
      const carousel = (
        <TypeRender heading={resolvedHeading} category={category} subcategory={subcategory} brandId={brandId} />
      );
      if (!puck.isEditing) {
        return carousel;
      }
      return (
        <EditorCarouselSlot>
          <EmptyCarouselNotice data-empty-notice>
            {`${
              typeLabel ?? 'This carousel'
            } has nothing to show for this store, so it won't appear on the published page.`}
          </EmptyCarouselNotice>
          {carousel}
        </EditorCarouselSlot>
      );
    }

    // Types without a renderer yet render nothing on the storefront — the same shape
    // a carousel already produces when its products query comes back empty, rather
    // than a new empty state shoppers can see.
    if (!puck.isEditing) {
      return <></>;
    }

    return (
      <EditorPlaceholder>
        <PlaceholderHeading>{resolvedHeading ?? UNTITLED_PLACEHOLDER_HEADING}</PlaceholderHeading>
        <span>
          {typeLabel === undefined
            ? 'Choose a Type in the settings panel.'
            : `${typeLabel} carousels are not supported yet.`}{' '}
          This block will not render on the published page.
        </span>
      </EditorPlaceholder>
    );
  },
};
